/**
 * Converts a number/string/Date to an ISO timestamp string.
 *
 * @param value - The value to convert.
 * @returns The ISO string, or null when unparseable.
 */
export function toIsoStringWithTimezone(value: unknown) {
   if (value === null || typeof value === 'undefined') {
      return null
   }

   let date: Date | null = null
   if (typeof value === 'number' && Number.isFinite(value)) {
      date = new Date(value)
   } else if (typeof value === 'string' && value) {
      const parsed = Date.parse(value)
      if (Number.isNaN(parsed)) {
         return null
      }
      date = new Date(parsed)
   } else if (value instanceof Date && Number.isFinite(value.getTime())) {
      date = value
   }

   if (!date || Number.isNaN(date.getTime())) {
      return null
   }

   return date.toISOString()
}

/**
 * Parses a number or date string into epoch milliseconds.
 *
 * @param value - The candidate value.
 * @returns Epoch milliseconds, or null.
 */
export function parseTimestampToNumber(value: unknown): number | null {
   if (typeof value === 'number' && Number.isFinite(value)) {
      return value
   }
   if (typeof value === 'string' && value) {
      const parsed = Date.parse(value)
      if (!Number.isNaN(parsed)) {
         return parsed
      }
   }
   return null
}

/**
 * Returns the epoch-ms start of midnight N days ago.
 *
 * @param days - Days to look back (defaults to 7).
 * @returns The cutoff timestamp in epoch ms.
 */
export function getDaysCutoff(days = 7) {
   const safeDays = Number.isFinite(days) ? Math.max(0, Math.round(days)) : 7
   const anchor = new Date()
   anchor.setHours(0, 0, 0, 0)
   anchor.setDate(anchor.getDate() - safeDays)
   return anchor.getTime()
}

/**
 * Returns the epoch-ms start of midnight N months ago.
 *
 * @param months - Months to look back (defaults to 1).
 * @returns The cutoff timestamp in epoch ms.
 */
export function getMonthCutoff(months = 1) {
   const safeMonths = Number.isFinite(months) ? Math.max(0, Math.round(months)) : 1
   const anchor = new Date()
   anchor.setHours(0, 0, 0, 0)
   anchor.setMonth(anchor.getMonth() - safeMonths)
   return anchor.getTime()
}

/**
 * Formats a timestamp as a `datetime-local` input value in local time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The `YYYY-MM-DDTHH:mm` string, or empty when invalid.
 */
export function formatDateTimeLocalValue(timestamp: number): string {
   const date = new Date(timestamp)
   if (Number.isNaN(date.getTime())) {
      return ''
   }

   const year = date.getFullYear()
   const month = String(date.getMonth() + 1).padStart(2, '0')
   const day = String(date.getDate()).padStart(2, '0')
   const hours = String(date.getHours()).padStart(2, '0')
   const minutes = String(date.getMinutes()).padStart(2, '0')
   return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * Parses a `datetime-local` input value into epoch milliseconds.
 *
 * @param value - The `YYYY-MM-DDTHH:mm` string.
 * @returns Epoch milliseconds, or null when empty or invalid.
 */
export function parseDateTimeLocalValue(value: string): number | null {
   if (!value) {
      return null
   }
   const parsed = new Date(value).getTime()
   return Number.isNaN(parsed) ? null : parsed
}
